import { HTTP_HEADERS } from '../contracts/http.js';
import { ChatApiError } from './errors.js';

/** `web`: navegador e Electron (cookies httpOnly); `app`: celular (tokens no corpo). */
export type ClientType = 'web' | 'app';

export type AuthTokens = {
  accessToken: string;
  refreshToken?: string;
  accessTokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
};

/** Onde o app guarda os tokens (Keychain/Keystore no celular). */
export interface TokenStore {
  get(): AuthTokens | null | Promise<AuthTokens | null>;
  set(tokens: AuthTokens | null): void | Promise<void>;
}

/** Arquivo para multipart: `Blob`/`File` no navegador, `{uri, type}` no React Native. */
export type UploadFile = {
  name: string;
  data: Blob | { uri: string; type?: string };
};

export type HttpOptions = {
  /** Raiz da API, com o `/api` (ex.: `https://discord.alternativamais.com.br/api` ou `/api`). */
  baseUrl: string;
  clientType: ClientType;
  /** Só no `app`. */
  tokens?: TokenStore;
  fetch?: typeof fetch;
  appVersion?: string;
  deviceId?: string;
  /** A sessão acabou (renovação recusada): a interface volta para a tela de entrar. */
  onSessionExpired?: () => void;
  /** Quantas vezes repetir depois de um 429 (padrão 3). */
  maxRateLimitRetries?: number;
  /** Espera (troca nos testes). */
  sleep?: (ms: number) => Promise<void>;
};

export type RequestOptions = {
  query?: Record<
    string,
    string | number | boolean | string[] | undefined | null
  >;
  body?: unknown;
  files?: UploadFile[];
  /** `X-Audit-Log-Reason` (vai codificado, como o Discord). */
  auditReason?: string | null;
  signal?: AbortSignal;
  /** Repete depois de 429 esperando o `retry_after` (padrão: sim). */
  retryRateLimit?: boolean;
};

/** 401 que a renovação resolve. */
const REFRESHABLE_REASONS = new Set([
  'token_missing',
  'token_expired',
  'token_invalid',
]);
/** Espera máxima de um 429 que ainda vale repetir sozinho (segundos). */
const MAX_AUTOMATIC_WAIT_S = 30;

/**
 * Transporte da API: cabeçalhos da base (`X-Client`, versão, dispositivo),
 * sessão por cookie (web) ou Bearer (app), renovação automática do access
 * token (uma por vez, as outras chamadas esperam), 429 com `retry_after` e
 * os erros no formato do Discord.
 */
export class HttpClient {
  private readonly fetchImpl: typeof fetch;
  private readonly sleep: (ms: number) => Promise<void>;
  private refreshing: Promise<boolean> | null = null;

  constructor(private readonly options: HttpOptions) {
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.sleep =
      options.sleep ??
      ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  }

  get clientType(): ClientType {
    return this.options.clientType;
  }

  get baseUrl(): string {
    return this.options.baseUrl.replace(/\/+$/, '');
  }

  async request<T>(
    method: string,
    path: string,
    options: RequestOptions = {},
  ): Promise<T> {
    let refreshed = false;
    let rateLimited = 0;
    for (;;) {
      const response = await this.send(method, path, options);
      if (response.ok) return (await readBody(response)) as T;
      const body = await readBody(response);
      const error = new ChatApiError(response.status, body);

      if (
        response.status === 401 &&
        !refreshed &&
        !path.startsWith('/auth/') &&
        REFRESHABLE_REASONS.has(error.reason ?? '')
      ) {
        refreshed = true;
        if (await this.refresh()) continue;
        throw error;
      }
      if (
        response.status === 429 &&
        options.retryRateLimit !== false &&
        rateLimited < (this.options.maxRateLimitRetries ?? 3)
      ) {
        const wait =
          error.retryAfter ?? Number(response.headers.get('Retry-After') ?? 1);
        if (wait <= MAX_AUTOMATIC_WAIT_S) {
          rateLimited++;
          await this.sleep(Math.ceil(wait * 1000));
          continue;
        }
      }
      throw error;
    }
  }

  /** Renova o access token; falhou, a sessão acabou. Uma renovação por vez. */
  refresh(): Promise<boolean> {
    this.refreshing ??= this.doRefresh().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    const tokens =
      this.options.clientType === 'app'
        ? await this.options.tokens?.get()
        : null;
    if (this.options.clientType === 'app' && !tokens?.refreshToken) {
      await this.expire();
      return false;
    }
    let response: Response;
    try {
      response = await this.send('POST', '/auth/refresh', {
        body:
          this.options.clientType === 'app'
            ? { refreshToken: tokens?.refreshToken }
            : {},
      });
    } catch {
      // Sem rede: não derruba a sessão, só esta chamada.
      return false;
    }
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        await this.expire();
      }
      return false;
    }
    if (this.options.clientType === 'app') {
      const body = (await readBody(response)) as AuthTokens;
      await this.options.tokens?.set({
        ...tokens,
        ...body,
        refreshToken: body.refreshToken ?? tokens?.refreshToken,
      } as AuthTokens);
    }
    return true;
  }

  private async expire() {
    await this.options.tokens?.set(null);
    this.options.onSessionExpired?.();
  }

  private async send(
    method: string,
    path: string,
    options: RequestOptions,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      [HTTP_HEADERS.client]: this.options.clientType,
    };
    if (this.options.appVersion) {
      headers[HTTP_HEADERS.appVersion] = this.options.appVersion;
    }
    if (this.options.deviceId) {
      headers[HTTP_HEADERS.device] = this.options.deviceId;
    }
    if (options.auditReason) {
      headers['X-Audit-Log-Reason'] = encodeURIComponent(options.auditReason);
    }
    if (this.options.clientType === 'app') {
      const tokens = await this.options.tokens?.get();
      if (tokens?.accessToken) {
        headers.Authorization = `Bearer ${tokens.accessToken}`;
      }
    }

    let body: BodyInit | undefined;
    if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
      body = options.body;
    } else if (options.files?.length) {
      const form = new FormData();
      if (options.body !== undefined) {
        form.append('payload_json', JSON.stringify(options.body));
      }
      options.files.forEach((file, index) => {
        const key = `files[${index}]`;
        if (file.data instanceof Blob) {
          form.append(key, file.data, file.name);
        } else {
          // React Native: o FormData aceita `{uri, name, type}`.
          form.append(key, {
            ...file.data,
            name: file.name,
          } as unknown as Blob);
        }
      });
      body = form;
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    try {
      return await this.fetchImpl(this.url(path, options.query), {
        method,
        headers,
        body,
        credentials: this.options.clientType === 'web' ? 'include' : 'omit',
        signal: options.signal,
      });
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') throw error;
      throw new ChatApiError(0, null, 'Sem conexão com o servidor.');
    }
  }

  private url(path: string, query?: RequestOptions['query']): string {
    const url = `${this.baseUrl}${path}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const item of value) params.append(key, item);
      } else {
        params.append(key, String(value));
      }
    }
    const text = params.toString();
    return text ? `${url}?${text}` : url;
  }
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  const type = response.headers.get('Content-Type') ?? '';
  if (type.includes('json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}
